/**
 * Main App - React Router Setup with Lazy Loading and Global Providers
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { ToastProvider } from './components/ui/Toast';

// Lazy load pages for better performance
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Payment = lazy(() => import('./pages/Payment'));
const Admin = lazy(() => import('./pages/Admin'));
const Profile = lazy(() => import('./pages/Profile'));
const Scan = lazy(() => import('./pages/Scan'));

// Yai Cal Animated Loader (Transparent & Bouncy)
function YaiCalLoader() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white/60 backdrop-blur-md relative z-50">
      <div className="flex flex-col items-center gap-8 animate-in fade-in zoom-in-95 duration-500">
        <div className="relative w-64 h-64 flex items-center justify-center">
          <img
            src="/yai_cal_loader_final.png"
            alt="Loading..."
            className="w-full h-full object-contain drop-shadow-xl animate-[bounce_2s_infinite] relative z-10"
            style={{ animationTimingFunction: 'cubic-bezier(0.28, 0.84, 0.42, 1)' }}
          />
        </div>

        <div className="text-center space-y-3">
          <h2 className="text-3xl font-bold text-slate-700 animate-pulse">
            รอแป๊บนึงน้า...
          </h2>
          <p className="text-emerald-600/80 text-lg font-medium">
            ยัยแคลกำลังฟิตกล้ามรอ 🥑💪
          </p>
        </div>
      </div>
    </div>
  );
}

// Component to handle LIFF routing via liff.state parameter
function LiffRouter() {
  const [searchParams] = useSearchParams();

  // Try multiple ways to get the page parameter
  const liffState = searchParams.get('liff.state');
  const page = searchParams.get('page');

  let targetPage = '';

  // Method 1: Check liff.state parameter
  if (liffState) {
    if (liffState.includes('=')) {
      targetPage = liffState.split('=').pop() || '';
    } else {
      targetPage = liffState;
    }
  }

  // Method 2: Check page parameter
  if (!targetPage && page) {
    targetPage = page;
  }

  // Route based on parameter
  if (targetPage === 'profile') {
    return <Profile />;
  }
  if (targetPage === 'payment') {
    return <Payment />;
  }
  if (targetPage === 'register') {
    return <Register />;
  }
  if (targetPage === 'scan') {
    return <Scan />;
  }
  // Default to dashboard
  return <Dashboard />;
}

function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <Suspense fallback={<YaiCalLoader />}>
          <Routes>
            {/* LIFF entry point - reads liff.state or page parameter */}
            <Route path="/" element={<LiffRouter />} />

            {/* Direct path routes (fallback) */}
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/scan" element={<Scan />} />

            {/* 404 - Redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
